import { ChangeEvent, useEffect, useMemo, useRef, useState } from "react";
import FormStep from "../../../services/models/formModels/MultiStepForm/formStep.model";
import CustomText from "../../atomic/CustomText";
import Button from "../../organism/Button";
import FileIcon from "../../../assets/svg/FileIcon";
import CloseIcon from "../../../assets/svg/CloseIcon";
import dataConstants from "../../../constants/dataConstants";
import toastMessages from "../../../services/utils/helpers/toastMessage";
import FileActions, {
  UploadToAzureResult,
} from "../../../services/utils/FileActions";
import { normalizeUploadFileName } from "../../../services/utils/helpers/fileNameNormalizer";
import useClassifySupportingDocuments from "../../../services/utils/custom hooks/Documents/useClassifySupportingDocuments";
import useGetPresignedUrl from "../../../services/utils/custom hooks/Documents/useGetPresignedUrl";
import { useOktaAuth } from "@okta/okta-react";
import {
  ClassifiedSupportingDocumentItem,
  ClassifiedSupportingDocuments,
} from "../../../services/models/API/responseModels/classifySupportingDocumentsResponse.model";
import DocumentIntelligenceProcessingModal, {
  DocumentIntelligenceProcessingStep,
} from "../../document-intelligence/DocumentIntelligenceProcessingModal";

type SupportingDocumentsKey =
  | "currentPrescriptionMedications"
  | "endoscopyReport"
  | "pathologyReport"
  | "insuranceCard"
  | "demographicsSheet"
  | "clinicNotes"
  | "requisitionForm"
  | "visitNotes"
  | "lomn"
  | "other";

type UploadStatus = "uploading" | "completed" | "error";

interface UploadedFileState {
  id: string;
  file: File;
  uploadPath?: string;
  viewUrl?: string;
  sizeInBytes?: number;
  status: UploadStatus;
  classificationLabels?: string[];
  errorMessage?: string;
}

const supportingDocumentKeys: SupportingDocumentsKey[] = [
  "currentPrescriptionMedications",
  "endoscopyReport",
  "pathologyReport",
  "insuranceCard",
  "demographicsSheet",
  "clinicNotes",
  "requisitionForm",
  "visitNotes",
  "lomn",
  "other",
];

const classificationLabelMap: Record<SupportingDocumentsKey, string> = {
  currentPrescriptionMedications: "Current Prescription Medications",
  endoscopyReport: "Endoscopy Report",
  pathologyReport: "Pathology Report",
  insuranceCard: "Insurance Card",
  demographicsSheet: "Demographics Sheet",
  clinicNotes: "Clinical Notes",
  requisitionForm: "Requisition Form",
  visitNotes: "Visit Note",
  lomn: "LOMN",
  other: "Other",
};

const backendLabelDisplayMap: Record<string, string> = {
  endoscopyReport: "Endoscopy Report",
  pathReport: "Pathology Report",
  patientDemo: "Patient Demographics",
  patientInsurance: "Insurance Card",
  requisitionForm: "Requisition Form",
  visitNoted: "Visit Note",
  visitNote: "Visit Note",
  lomn: "LOMN",
  LOMN: "LOMN",
  other: "Other",
};

const responseKeyToSupportingKey: Record<string, SupportingDocumentsKey> = {
  currentPrescriptionMedications: "currentPrescriptionMedications",
  currentPrescriptionMedication: "currentPrescriptionMedications",
  endoscopyReport: "endoscopyReport",
  pathReport: "pathologyReport",
  pathologyReport: "pathologyReport",
  insuranceCard: "insuranceCard",
  patientInsurance: "insuranceCard",
  demographicsSheet: "demographicsSheet",
  patientDemo: "demographicsSheet",
  clinicNotes: "clinicNotes",
  requisitionForm: "requisitionForm",
  visitNotes: "visitNotes",
  visitNote: "visitNotes",
  visitNoted: "visitNotes",
  lomn: "lomn",
  LOMN: "lomn",
  other: "other",
};

const fileActions = new FileActions();
const supportingDocumentRequiredMessage =
  "Please upload at least one supporting document when Attach Insurance In Supporting Documents is selected.";
const viewUrlStorageKey = "supportingDocumentsViewUrlMap";
const displayNameStorageKey = "supportingDocumentsDisplayNameMap";
const fileSizeStorageKey = "supportingDocumentsFileSizeMap";
const classificationLabelsStorageKey = "supportingDocumentsClassificationLabelsMap";
const activePathsStorageKey = "supportingDocumentsActivePaths";

function getNormalizedPath(path: string): string {
  return decodeURIComponent(path).replace(/^\/+/, "").toLowerCase();
}

function pathsMatch(pathA: string, pathB: string): boolean {
  const a = getNormalizedPath(pathA);
  const b = getNormalizedPath(pathB);
  return a === b || a.endsWith(b) || b.endsWith(a);
}

function getUniquePaths(paths: string[]): string[] {
  const normalizedToOriginal = new Map<string, string>();
  paths.filter(Boolean).forEach((path) => {
    normalizedToOriginal.set(getNormalizedPath(path), path);
  });
  return Array.from(normalizedToOriginal.values());
}

function getFileNameFromPath(path: string): string {
  if (!path) return "document";
  const normalizedPath = decodeURIComponent(path).split("?")[0];
  const segments = normalizedPath.split("/").filter(Boolean);
  return segments[segments.length - 1] || "document";
}

function getPresignedRequestFileName(path: string): string {
  const fileName = getFileNameFromPath(path);
  const lastDotIndex = fileName.lastIndexOf(".");
  if (lastDotIndex <= 0) return fileName;

  const base = fileName.slice(0, lastDotIndex);
  const extension = fileName.slice(lastDotIndex);

  // Keep single upload timestamp suffixes, but collapse duplicated suffixes:
  // `name_123_456.pdf` -> `name_123.pdf`
  const normalizedBase = base.replace(/(_\d{10,})_\d{10,}$/, "$1");

  return `${normalizedBase}${extension}`;
}

function getStoredViewUrlMap(): Record<string, string> {
  try {
    const raw = sessionStorage.getItem(viewUrlStorageKey);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return typeof parsed === "object" && parsed !== null ? parsed : {};
  } catch {
    return {};
  }
}

function saveStoredViewUrl(path: string, url: string): void {
  if (!path || !url) return;
  const existing = getStoredViewUrlMap();
  existing[path] = url;
  sessionStorage.setItem(viewUrlStorageKey, JSON.stringify(existing));
}

function stripTimestampSuffix(fileName: string): string {
  const lastDotIndex = fileName.lastIndexOf(".");
  if (lastDotIndex <= 0) return fileName;
  const base = fileName.slice(0, lastDotIndex);
  const extension = fileName.slice(lastDotIndex);
  return `${base.replace(/_\d{10,}$/, "")}${extension}`;
}

function getStoredDisplayNameMap(): Record<string, string> {
  try {
    const raw = sessionStorage.getItem(displayNameStorageKey);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return typeof parsed === "object" && parsed !== null ? parsed : {};
  } catch {
    return {};
  }
}

function saveStoredDisplayName(path: string, fileName: string): void {
  if (!path || !fileName) return;
  const existing = getStoredDisplayNameMap();
  existing[path] = fileName;
  sessionStorage.setItem(displayNameStorageKey, JSON.stringify(existing));
}

function getDisplayFileName(path: string): string {
  const stored = getStoredDisplayNameMap()[path];
  if (stored) return stored;
  return stripTimestampSuffix(getFileNameFromPath(path));
}

function getStoredFileSizeMap(): Record<string, number> {
  try {
    const raw = sessionStorage.getItem(fileSizeStorageKey);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return typeof parsed === "object" && parsed !== null ? parsed : {};
  } catch {
    return {};
  }
}

function saveStoredFileSize(path: string, sizeInBytes: number): void {
  if (!path || Number.isNaN(sizeInBytes)) return;
  const existing = getStoredFileSizeMap();
  existing[path] = Math.max(0, sizeInBytes);
  sessionStorage.setItem(fileSizeStorageKey, JSON.stringify(existing));
}

function getStoredClassificationLabelsMap(): Record<string, string[]> {
  try {
    const raw = sessionStorage.getItem(classificationLabelsStorageKey);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return typeof parsed === "object" && parsed !== null ? parsed : {};
  } catch {
    return {};
  }
}

function getStoredClassificationLabelsForPath(
  path: string,
  storedLabelsMap: Record<string, string[]>
): string[] {
  const normalizedPath = getNormalizedPath(path);
  const directMatch = storedLabelsMap[normalizedPath];
  if (directMatch) return directMatch;

  const matchedKey = Object.keys(storedLabelsMap).find((storedPath) => {
    const normalizedStoredPath = getNormalizedPath(storedPath);
    return (
      normalizedStoredPath === normalizedPath ||
      normalizedStoredPath.endsWith(normalizedPath) ||
      normalizedPath.endsWith(normalizedStoredPath)
    );
  });

  return matchedKey ? storedLabelsMap[matchedKey] ?? [] : [];
}

function saveStoredClassificationLabels(
  labelsByPath: Record<string, string[]>
): void {
  if (!labelsByPath || Object.keys(labelsByPath).length === 0) return;
  const existing = getStoredClassificationLabelsMap();
  const merged: Record<string, string[]> = { ...existing };

  Object.keys(labelsByPath).forEach((path) => {
    if (!path) return;
    const normalizedPath = getNormalizedPath(path);
    merged[normalizedPath] = Array.from(
      new Set([
        ...(existing[normalizedPath] ?? []),
        ...(labelsByPath[path] ?? []),
      ])
    );
  });

  sessionStorage.setItem(classificationLabelsStorageKey, JSON.stringify(merged));
}

function removeStoredPathFromMap({
  storageKey,
  pathToRemove,
}: {
  storageKey: string;
  pathToRemove: string;
}): void {
  if (!pathToRemove) return;
  try {
    const raw = sessionStorage.getItem(storageKey);
    if (!raw) return;
    const parsed = JSON.parse(raw);
    if (typeof parsed !== "object" || parsed === null) return;

    const mutableMap = { ...(parsed as Record<string, any>) };
    const normalizedPathToRemove = getNormalizedPath(pathToRemove);
    Object.keys(mutableMap).forEach((storedPath) => {
      if (pathsMatch(storedPath, normalizedPathToRemove)) {
        delete mutableMap[storedPath];
      }
    });
    sessionStorage.setItem(storageKey, JSON.stringify(mutableMap));
  } catch {
    // no-op
  }
}

/** Prefer `container`/`filename`; never persist SAS URLs — use `documents/...` for session + payload alignment. */
function getPathFromDocumentValue(value: any): string | undefined {
  if (!value) return undefined;
  let raw: string | undefined;
  if (typeof value === "string") {
    raw = value.trim();
  } else if (value?.container && value?.filename) {
    raw = `${String(value.container).replace(/^\/+/, "")}/${String(value.filename).replace(/^\/+/, "")}`;
  } else if (typeof value?.url === "string") {
    raw = value.url.trim();
  }
  if (!raw) return undefined;
  const decoded = decodeURIComponent(raw);
  if (/^https?:\/\//i.test(decoded)) {
    try {
      const pathname = new URL(decoded).pathname.replace(/^\/+/, "");
      return pathname || undefined;
    } catch {
      return undefined;
    }
  }
  const noQuery = decoded.split("?")[0].replace(/^\/+/, "");
  return noQuery || undefined;
}

function getAllPathsFromSupportingDocuments(
  supportingDocuments: Record<string, any>
): string[] {
  return Object.values(supportingDocuments ?? {}).flatMap((value: any) => {
    if (!value) return [];
    if (Array.isArray(value)) {
      return value
        .map((item) => getPathFromDocumentValue(item))
        .filter((path): path is string => Boolean(path));
    }
    const path = getPathFromDocumentValue(value);
    return path ? [path] : [];
  });
}

function saveActiveSupportingDocumentPaths(paths: string[]): void {
  const uniquePaths = getUniquePaths(paths);
  sessionStorage.setItem(activePathsStorageKey, JSON.stringify(uniquePaths));
}

function getStoredActivePathsFromStorage(): string[] {
  try {
    const raw = sessionStorage.getItem(activePathsStorageKey);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed)
      ? parsed.filter((p): p is string => typeof p === "string" && Boolean(p))
      : [];
  } catch {
    return [];
  }
}

/** Union form paths with session active paths so update payloads are not stripped in OnlineOrderActions. */
function mergeActivePathsFromForm(
  supportingDocuments: Record<string, any> | undefined
): void {
  const fromForm = getAllPathsFromSupportingDocuments(supportingDocuments ?? {});
  const fromStorage = getStoredActivePathsFromStorage();
  saveActiveSupportingDocumentPaths([...fromStorage, ...fromForm]);
}

function extractSupportingDocumentPathsFromValue(value: unknown): string[] {
  if (!value) return [];
  if (Array.isArray(value)) {
    return value
      .map((item) => getPathFromDocumentValue(item))
      .filter((path): path is string => Boolean(path));
  }
  const path = getPathFromDocumentValue(value as any);
  return path ? [path] : [];
}

/** Union per-key path arrays so classification does not replace existing attachments. */
function mergeSupportingDocumentsWithClassifyPatch(
  existing: Record<string, any> | undefined,
  patch: Partial<ClassifiedSupportingDocuments> | Record<string, any>
): Record<string, any> {
  const base: Record<string, any> = { ...(existing ?? {}) };
  const patchKeys = Object.keys(patch ?? {}) as Array<keyof typeof patch>;

  patchKeys.forEach((key) => {
    const patchVal = patch[key];
    if (patchVal === undefined || patchVal === null) return;
    if (!Array.isArray(patchVal)) return;

    const existingVal = base[key as string];
    const existingPaths = extractSupportingDocumentPathsFromValue(existingVal);
    const patchPaths = patchVal.filter(
      (p): p is string => typeof p === "string" && Boolean(p)
    );
    base[key as string] = getUniquePaths([...existingPaths, ...patchPaths]);
  });

  return base;
}

function toStorageDocumentPath(path: string): string {
  if (!path) return "";

  const decodedPath = decodeURIComponent(path).trim();
  if (!decodedPath) return "";

  try {
    if (/^https?:\/\//i.test(decodedPath)) {
      const parsedUrl = new URL(decodedPath);
      return parsedUrl.pathname.replace(/^\/+/, "");
    }
  } catch {
    // Fallback to string-based normalization when URL parsing fails.
  }

  return decodedPath.split("?")[0].replace(/^\/+/, "");
}

function isHttpUrlPath(path: string): boolean {
  return /^https?:\/\//i.test(path.trim());
}

function sanitizeClassifyDocumentPaths(paths: string[]): string[] {
  const sanitizedPaths = paths
    .map((path) => (typeof path === "string" ? path.trim() : ""))
    .filter(Boolean)
    .filter((path) => !isHttpUrlPath(path))
    .map((path) => toStorageDocumentPath(path))
    .filter((path) => /^documents\//i.test(path));

  return getUniquePaths(sanitizedPaths);
}

function getExistingSupportingDocumentPaths(
  supportingDocuments: Record<string, any> | undefined
): string[] {
  if (!supportingDocuments) return [];

  return supportingDocumentKeys.flatMap((key) => {
    const documents = supportingDocuments[key];
    if (!Array.isArray(documents)) return [];
    return documents
      .map((document) => getPathFromDocumentValue(document))
      .filter((path): path is string => Boolean(path))
      .map((path) => toStorageDocumentPath(path))
      .filter(Boolean);
  });
}

function normalizeClassifiedDocumentsPatch(
  supportingDocuments:
    | Partial<ClassifiedSupportingDocuments>
    | Record<string, string[] | null>
    | undefined
): Partial<ClassifiedSupportingDocuments> {
  if (!supportingDocuments) return {};

  const normalized: Partial<ClassifiedSupportingDocuments> = {};
  const supportingDocumentsByKey = supportingDocuments as Record<
    string,
    string[] | null | undefined
  >;
  Object.keys(supportingDocumentsByKey).forEach((responseKey) => {
    const mappedKey = responseKeyToSupportingKey[responseKey];
    if (!mappedKey) return;
    const value = supportingDocumentsByKey[responseKey];
    if (Array.isArray(value)) {
      normalized[mappedKey] = value.filter(Boolean);
    }
  });

  return normalized;
}

function getDisplayLabel({
  responseKey,
  docType,
}: {
  responseKey?: string;
  docType?: string;
}): string {
  if (responseKey && backendLabelDisplayMap[responseKey]) {
    return backendLabelDisplayMap[responseKey];
  }
  if (docType && docType.trim().length > 0) return docType.trim();
  return "Other";
}

function parseClassifiedDocumentsResponse(data: any): {
  normalizedSupportingDocumentsPatch: Partial<ClassifiedSupportingDocuments>;
  labelsByPath: Record<string, string[]>;
} {
  const normalizedSupportingDocumentsPatch: Partial<ClassifiedSupportingDocuments> = {};
  const labelsByPathMap = new Map<string, Set<string>>();

  const documents = Array.isArray(data?.documents)
    ? (data.documents as ClassifiedSupportingDocumentItem[])
    : [];

  if (documents.length > 0) {
    const supportingDocumentsMap = new Map<
      SupportingDocumentsKey,
      Set<string>
    >();
    documents.forEach((document) => {
      const path = document?.key;
      if (!path) return;

      const pageItems = Array.isArray(document?.pages) ? document.pages : [];
      pageItems.forEach((page) => {
        const responseKey = page?.groupKey;
        const mappedKey = responseKeyToSupportingKey[responseKey];
        if (mappedKey) {
          if (!supportingDocumentsMap.has(mappedKey)) {
            supportingDocumentsMap.set(mappedKey, new Set<string>());
          }
          supportingDocumentsMap.get(mappedKey)?.add(path);
        }

        const displayLabel = getDisplayLabel({
          responseKey,
          docType: page?.docType,
        });
        if (!labelsByPathMap.has(path)) {
          labelsByPathMap.set(path, new Set<string>());
        }
        labelsByPathMap.get(path)?.add(displayLabel);
      });
    });

    supportingDocumentsMap.forEach((pathsSet, key) => {
      normalizedSupportingDocumentsPatch[key] = Array.from(pathsSet.values());
    });
  } else {
    const normalizedLegacyPatch = normalizeClassifiedDocumentsPatch(
      data?.supportingDocuments
    );
    Object.assign(normalizedSupportingDocumentsPatch, normalizedLegacyPatch);
    const classifiedSupportingDocumentsByKey = (data?.supportingDocuments ??
      {}) as Record<string, string[] | null | undefined>;

    Object.keys(classifiedSupportingDocumentsByKey).forEach((responseKey) => {
      const paths = classifiedSupportingDocumentsByKey[responseKey] ?? [];
      const displayLabel = getDisplayLabel({ responseKey });
      paths.forEach((path) => {
        if (!path) return;
        if (!labelsByPathMap.has(path)) {
          labelsByPathMap.set(path, new Set<string>());
        }
        labelsByPathMap.get(path)?.add(displayLabel);
      });
    });
  }

  const labelsByPath: Record<string, string[]> = {};
  labelsByPathMap.forEach((labelsSet, path) => {
    labelsByPath[path] = Array.from(labelsSet.values());
  });

  return { normalizedSupportingDocumentsPatch, labelsByPath };
}

function hasAnySupportingDocument(value: any): boolean {
  if (!value) return false;
  if (Array.isArray(value)) {
    return value.filter(Boolean).length > 0;
  }
  if (value instanceof File) return true;
  if (typeof value === "string") return value.trim().length > 0;
  if (value?.container && value?.filename) return true;
  return false;
}

function SupportingDocumentsStep({
  setFieldValue,
  setFieldTouched,
  validateForm,
  values,
}: FormStep): JSX.Element {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFileState[]>([]);
  const [isProcessing, setIsProcessing] =
    useState(false);
  const [processingStep, setProcessingStep] =
    useState<DocumentIntelligenceProcessingStep>("uploading");
  const [progressPercent, setProgressPercent] = useState(0);
  const [showProcessingModal, setShowProcessingModal] = useState(false);
  const [classificationError, setClassificationError] = useState<string | null>(
    null
  );
  const [lastDocumentsForRetry, setLastDocumentsForRetry] = useState<string[]>(
    []
  );
  const classifyDocuments = useClassifySupportingDocuments();
  const getPresignedUrl = useGetPresignedUrl();
  const { authState } = useOktaAuth();
  const attachInsuranceInSupportingDocuments =
    Number(values?.billingInfo?.primaryInsuranceId) === 1;
  const hasPersistedSupportingDocument = useMemo(() => {
    if (values?.isSupportingDocuments) return true;
    const supportingDocuments = values?.supportingDocuments ?? {};
    return Object.values(supportingDocuments).some(hasAnySupportingDocument);
  }, [values?.isSupportingDocuments, values?.supportingDocuments]);
  const showSupportingDocumentRequiredMessage =
    attachInsuranceInSupportingDocuments &&
    !hasPersistedSupportingDocument &&
    !isProcessing;

  useEffect(() => {
    void validateForm?.();
  }, [values?.billingInfo?.primaryInsuranceId, validateForm]);

  const canRetryClassification =
    !isProcessing && lastDocumentsForRetry.length > 0;

  const validateFile = (file: File): boolean => {
    const extension = file.name.split(".").pop()?.toLowerCase();
    const sizeInMB = file.size / 1024 / 1024;

    if (!dataConstants.validTypes.includes(extension ?? "")) {
      toastMessages.error(`Invalid file format: ${file.name}`);
      return false;
    }
    if (sizeInMB > 55) {
      toastMessages.error(`File size limit exceeded: ${file.name}`);
      return false;
    }
    return true;
  };

  const classifyAndPersist = async (documents: string[]) => {
    const sanitizedDocuments = sanitizeClassifyDocumentPaths(documents);
    if (sanitizedDocuments.length === 0) return;
    setShowProcessingModal(true);
    setIsProcessing(true);
    setProcessingStep("analyzing");
    setProgressPercent(75);
    setClassificationError(null);
    setLastDocumentsForRetry(sanitizedDocuments);
    try {
      const response = await classifyDocuments.mutateAsync({
        documents: sanitizedDocuments,
      });
      const { normalizedSupportingDocumentsPatch, labelsByPath } =
        parseClassifiedDocumentsResponse(response?.data);
      saveStoredClassificationLabels(labelsByPath);
      const mergedSupportingDocuments = mergeSupportingDocumentsWithClassifyPatch(
        values?.supportingDocuments,
        normalizedSupportingDocumentsPatch
      );
      mergeActivePathsFromForm(mergedSupportingDocuments);
      const updatedValues = {
        ...(values ?? {}),
        supportingDocuments: mergedSupportingDocuments,
        isSupportingDocuments: true,
      };
      setProcessingStep("extracting");
      setProgressPercent(95);
      await setFieldValue?.(
        "supportingDocuments",
        mergedSupportingDocuments,
        false
      );
      await setFieldValue?.("isSupportingDocuments", true, false);
      setFieldTouched?.("supportingDocuments", true, false);
      await validateForm?.(updatedValues);

      setUploadedFiles((prevState) =>
        prevState.map((item) => {
          if (!item.uploadPath) return item;
          const matchedPath = Object.keys(labelsByPath).find((classifiedPath) =>
            pathsMatch(item.uploadPath ?? "", classifiedPath)
          );
          const matchedLabels = matchedPath ? labelsByPath[matchedPath] : [];
          const mergedLabels = Array.from(
            new Set([...(item.classificationLabels ?? []), ...matchedLabels])
          );

          return {
            ...item,
            // Preserve existing classifications if this response doesn't include this file.
            classificationLabels:
              mergedLabels.length > 0 ? mergedLabels : item.classificationLabels,
          };
        })
      );
      setProgressPercent(100);
      globalThis.setTimeout(() => {
        setShowProcessingModal(false);
      }, 350);
    } catch (error: any) {
      const message =
        error?.response?.data?.message ??
        error?.message ??
        "Unable to classify uploaded documents";
      setClassificationError(message);
    } finally {
      setIsProcessing(false);
    }
  };

  const processFiles = async (selectedFiles: File[]) => {
    if (!authState?.accessToken?.accessToken) {
      toastMessages.error("Authentication is required to upload files.");
      return;
    }

    const validatedFiles = selectedFiles.filter(validateFile);
    if (validatedFiles.length === 0) return;

  const normalizedFiles = validatedFiles.map((file) => {
    const normalizedName = normalizeUploadFileName(file.name);
    return new File([file], normalizedName, {
      type: file.type,
      lastModified: file.lastModified,
    });
  });

    setShowProcessingModal(true);
    setProcessingStep("uploading");
    setProgressPercent(10);
    setClassificationError(null);

  const newRows: UploadedFileState[] = normalizedFiles.map((file, index) => ({
      id: `${file.name}-${Date.now()}-${index}`,
      file,
      sizeInBytes: file.size,
      status: "uploading",
    }));
    setUploadedFiles((prevState) => [...prevState, ...newRows]);

    const uploadResults = await Promise.allSettled(
    normalizedFiles.map((file) =>
        fileActions.uploadToAzureWithMetadata({
          type: "document",
          file,
          token: authState?.accessToken?.accessToken ?? "",
        })
      )
    );
    const successfulDocuments = uploadResults
      .filter(
        (result): result is PromiseFulfilledResult<UploadToAzureResult> =>
          result.status === "fulfilled"
      )
      .map((result) => result.value.filepath)
      .filter(Boolean);

    if (successfulDocuments.length > 0) {
      setProgressPercent(65);
    }

    setUploadedFiles((prevState) =>
      prevState.map((row) => {
        const rowIndex = newRows.findIndex((newRow) => newRow.id === row.id);
        if (rowIndex === -1) return row;
        const result = uploadResults[rowIndex];

        if (result.status === "fulfilled") {
          saveStoredViewUrl(result.value.filepath, result.value.presignedUrl);
          saveStoredDisplayName(result.value.filepath, row.file.name);
          saveStoredFileSize(result.value.filepath, row.file.size);
          return {
            ...row,
            status: "completed",
            uploadPath: result.value.filepath,
            viewUrl: result.value.presignedUrl,
            sizeInBytes: row.file.size,
          };
        }

        return {
          ...row,
          status: "error",
          errorMessage:
            result.reason?.message ?? "Upload failed for this document.",
        };
      })
    );

    const pathsReadyForClassification = successfulDocuments;

    if (pathsReadyForClassification.length > 0) {
      const existingDocumentPathSet = new Set(
        sanitizeClassifyDocumentPaths(
          getExistingSupportingDocumentPaths(values?.supportingDocuments)
        ).map((path) => getNormalizedPath(path))
      );
      const documentsForClassification = sanitizeClassifyDocumentPaths(
        pathsReadyForClassification
      ).filter((path) => !existingDocumentPathSet.has(getNormalizedPath(path)));

      if (documentsForClassification.length > 0) {
        await classifyAndPersist(documentsForClassification);
      } else {
        setShowProcessingModal(false);
      }
    } else {
      setShowProcessingModal(false);
    }
  };

  const handleInputChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = event.target.files ? Array.from(event.target.files) : [];
    if (selectedFiles.length === 0) return;
    await processFiles(selectedFiles);
    event.target.value = "";
  };

  const handleDrop = async (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    const selectedFiles = event.dataTransfer?.files
      ? Array.from(event.dataTransfer.files)
      : [];
    if (selectedFiles.length === 0) return;
    await processFiles(selectedFiles);
  };

  const statusTextMap = useMemo(
    () => ({
      uploading: "Uploading...",
      completed: "Uploaded",
      error: "Upload failed",
    }),
    []
  );

  const handleViewFile = async (document: UploadedFileState) => {
    if (document.viewUrl) {
      window.open(document.viewUrl, "_blank", "noopener,noreferrer");
      return;
    }

    if (document.uploadPath) {
      const storedViewUrl = getStoredViewUrlMap()[document.uploadPath];
      if (storedViewUrl) {
        setUploadedFiles((prevState) =>
          prevState.map((item) =>
            item.id === document.id ? { ...item, viewUrl: storedViewUrl } : item
          )
        );
        window.open(storedViewUrl, "_blank", "noopener,noreferrer");
        return;
      }
      try {
        const fileName = getPresignedRequestFileName(document.uploadPath);
        const presignedUrlResponse = await getPresignedUrl.mutateAsync(fileName);
        const presignedUrl = presignedUrlResponse?.data?.url;

        if (presignedUrl) {
          saveStoredViewUrl(document.uploadPath, presignedUrl);
          setUploadedFiles((prevState) =>
            prevState.map((item) =>
              item.id === document.id ? { ...item, viewUrl: presignedUrl } : item
            )
          );
          window.open(presignedUrl, "_blank", "noopener,noreferrer");
          return;
        }

        toastMessages.error("Unable to open file");
        return;
      } catch (error: any) {
        toastMessages.error(
          error?.response?.data?.message ??
            error?.message ??
            "Unable to open file"
        );
        return;
      }
    }

    if (document.file instanceof File) {
      const localFileUrl = URL.createObjectURL(document.file);
      window.open(localFileUrl, "_blank", "noopener,noreferrer");
    }
  };

  useEffect(() => {
    const supportingDocuments = values?.supportingDocuments ?? {};
    const storedClassificationLabels = getStoredClassificationLabelsMap();
    const restoredFilesByPath = new Map<string, UploadedFileState>();

    supportingDocumentKeys.forEach((key) => {
      const documents = supportingDocuments[key];
      if (!Array.isArray(documents)) return;

      documents.filter(Boolean).forEach((document: any, index: number) => {
        const path = getPathFromDocumentValue(document);
        if (!path) return;
        const existing = restoredFilesByPath.get(path);
        const existingLabels = existing?.classificationLabels ?? [];
        const persistedLabels = getStoredClassificationLabelsForPath(
          path,
          storedClassificationLabels
        );
        const mergedLabels = Array.from(
          new Set([...existingLabels, ...persistedLabels, classificationLabelMap[key]])
        );
        const fileName = getDisplayFileName(path);
        const storedFileSize = getStoredFileSizeMap()[path];
        restoredFilesByPath.set(path, {
          id: existing?.id ?? `restored-${key}-${index}-${getNormalizedPath(path)}`,
          file: existing?.file ?? new File([], fileName),
          uploadPath: path,
          sizeInBytes:
            typeof document?.size === "number" && document.size >= 0
              ? document.size
              : (existing?.sizeInBytes ?? 0) > 0
              ? existing?.sizeInBytes
              : typeof storedFileSize === "number"
              ? storedFileSize
              : 0,
          status: "completed",
          classificationLabels: mergedLabels,
        });
      });
    });
    const restoredFiles = Array.from(restoredFilesByPath.values());

    setUploadedFiles((prevState) => {
      if (restoredFiles.length === 0) return prevState;
      const existingPathSet = new Set(
        prevState
          .map((item) => item.uploadPath)
          .filter((path): path is string => Boolean(path))
          .map(getNormalizedPath)
      );

      const uniqueRestoredFiles = restoredFiles.filter(
        (item) =>
          !!item.uploadPath &&
          !existingPathSet.has(getNormalizedPath(item.uploadPath))
      );

      if (prevState.length === 0) return uniqueRestoredFiles;
      if (uniqueRestoredFiles.length === 0) return prevState;

      return [...prevState, ...uniqueRestoredFiles];
    });

    mergeActivePathsFromForm(values?.supportingDocuments);
  }, [values?.supportingDocuments]);

  const handleRemoveFile = async (documentId: string) => {
    const documentToRemove = uploadedFiles.find((item) => item.id === documentId);
    if (!documentToRemove) return;

    const remainingFiles = uploadedFiles.filter((item) => item.id !== documentId);
    setUploadedFiles(remainingFiles);

    if (!documentToRemove.uploadPath) return;
    removeStoredPathFromMap({
      storageKey: viewUrlStorageKey,
      pathToRemove: documentToRemove.uploadPath,
    });
    removeStoredPathFromMap({
      storageKey: displayNameStorageKey,
      pathToRemove: documentToRemove.uploadPath,
    });
    removeStoredPathFromMap({
      storageKey: fileSizeStorageKey,
      pathToRemove: documentToRemove.uploadPath,
    });
    removeStoredPathFromMap({
      storageKey: classificationLabelsStorageKey,
      pathToRemove: documentToRemove.uploadPath,
    });

    const currentSupportingDocuments = values?.supportingDocuments ?? {};
    const updatedSupportingDocuments: Record<string, any> = {
      ...currentSupportingDocuments,
    };

    Object.keys(currentSupportingDocuments).forEach((key) => {
      const existingDocuments = currentSupportingDocuments[key];
      if (!Array.isArray(existingDocuments)) return;
      updatedSupportingDocuments[key] = existingDocuments.filter(
        (document: any) => {
          const path = getPathFromDocumentValue(document);
          if (!path) return true;
          return !pathsMatch(path, documentToRemove.uploadPath ?? "");
        }
      );
    });

    const hasAnyDocumentAfterRemoval = Object.keys(updatedSupportingDocuments).some(
      (key) =>
      hasAnySupportingDocument(updatedSupportingDocuments[key])
    );

    await setFieldValue?.("supportingDocuments", updatedSupportingDocuments, false);
    await setFieldValue?.("isSupportingDocuments", hasAnyDocumentAfterRemoval, false);
    saveActiveSupportingDocumentPaths(
      getAllPathsFromSupportingDocuments(updatedSupportingDocuments)
    );
    setFieldTouched?.("supportingDocuments", true, false);
    void validateForm?.();
  };

  return (
    <div className="w-full">
      <div
        className="bg-sideNavGray border-2 border-dashed border-roundedInputBorder rounded-[8px] p-6"
        onDragOver={(event) => event.preventDefault()}
        onDrop={handleDrop}
      >
        <CustomText
          text="Upload or drag and drop documents"
          fontWeight="font-medium"
          className="text-royalBlue text-center mb-2"
        />
        <CustomText
          text="Supports multiple files. Accepted formats: .png, .jpeg, .pdf, .doc, .docx"
          fontWeight="font-normal"
          fontSize="text-sm"
          className="text-darkGray text-center mb-4"
        />
        <div className="flex justify-center">
          <Button
            type="button"
            buttonTheme="secondary"
            textInformation={{
              text: "Choose Files",
              fontWeight: "font-semibold",
              fontSize: "text-sm",
            }}
            className="bg-white"
            onClick={() => inputRef.current?.click()}
          />
        </div>
        <input
          type="file"
          multiple
          hidden
          ref={inputRef}
          onChange={handleInputChange}
          accept=".png,.jpeg,.jpg,.pdf,.doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
        />
      </div>

      {showSupportingDocumentRequiredMessage && (
        <div className="mt-4 rounded-md border border-requiredRed bg-white p-3">
          <CustomText
            text={supportingDocumentRequiredMessage}
            fontWeight="font-medium"
            fontSize="text-sm"
            className="text-requiredRed"
          />
        </div>
      )}

      {uploadedFiles.length > 0 && (
        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
          {uploadedFiles.map((document) => (
            <div
              key={document.id}
              className="border border-roundedInputBorder rounded-lg bg-white p-3 shadow-sm"
            >
              <div className="flex items-start justify-between gap-2">
                <div className="flex min-w-0 items-center gap-2">
                  <div className="flex h-6 w-6 shrink-0 items-center justify-center">
                    <FileIcon className="h-6 w-6 shrink-0" />
                  </div>
                  <div className="min-w-0">
                    <CustomText
                      text={document.file.name}
                      fontWeight="font-medium"
                      fontSize="text-sm"
                      className="text-royalBlue truncate"
                    />
                    <CustomText
                      text={`${Math.round(
                        (document.sizeInBytes ?? document.file.size ?? 0) / 1024
                      )} kb`}
                      fontWeight="font-normal"
                      fontSize="text-xs"
                      className="text-darkGray"
                    />
                  </div>
                </div>
                <button
                  type="button"
                  className="flex h-6 w-6 shrink-0 items-center justify-center rounded hover:bg-sideNavGray"
                  onClick={() => handleRemoveFile(document.id)}
                  aria-label={`Remove ${document.file.name}`}
                >
                  <CloseIcon className="h-3.5 w-3.5" />
                </button>
              </div>

              <div className="mt-3 flex items-center justify-between">
                <CustomText
                  text={
                    document.status === "error" && document.errorMessage
                      ? document.errorMessage
                      : statusTextMap[document.status]
                  }
                  fontWeight="font-medium"
                  fontSize="text-xs"
                  className={
                    document.status === "error"
                      ? "text-requiredRed"
                      : "text-darkGray"
                  }
                />
                {document.classificationLabels &&
                  document.classificationLabels.length > 0 && (
                  <div className="flex flex-wrap items-center justify-end gap-1">
                    {document.classificationLabels.map((label) => (
                      <div
                        key={`${document.id}-${label}`}
                        className="inline-flex items-center rounded-full bg-lightBlue px-2 py-1"
                      >
                        <span className="mr-1 inline-block h-2 w-2 rounded-full bg-primary" />
                        <CustomText
                          text={label}
                          fontWeight="font-medium"
                          fontSize="text-xs"
                          className="text-royalBlue"
                        />
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="mt-2">
                <button
                  type="button"
                  className="text-xs text-activeLink font-medium"
                  onClick={() => {
                    void handleViewFile(document);
                  }}
                >
                  View file
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <DocumentIntelligenceProcessingModal
        visible={showProcessingModal}
        embedded={false}
        step={processingStep}
        currentIndex={uploadedFiles.length}
        total={uploadedFiles.length}
        progressPercent={progressPercent}
        error={classificationError}
        inProgress={isProcessing}
        onClose={() => {
          setShowProcessingModal(false);
          setIsProcessing(false);
        }}
        onRetry={
          canRetryClassification
            ? () => classifyAndPersist(lastDocumentsForRetry)
            : undefined
        }
      />
    </div>
  );
}

export default SupportingDocumentsStep;
